import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'

import UsersService from '../services/UsersService'

import { DEFAULT_ROLE, USER_ROLES } from '../config/users'

import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { PlusCircle, Save2, Trash } from 'react-bootstrap-icons'

const UsersForm = (props) => {

    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [name, setName] = useState('')
    const [vat, setVat] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [emailErrorKey, setEmailErrorKey] = useState('usersEmailInvalid')
    const [password, setPassword] = useState('')
    const [passErrorKey, setPassErrorKey] = useState('usersPasswordInvalid')
    const [role, setRole] = useState(DEFAULT_ROLE)
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    const getData = async () => {
        await UsersService.getUser(elementId).then(
            response => {
                setName(response.data.name || '')
                setVat(response.data.vat || '')
                setPhone(response.data.phone || '')
                setEmail(response.data.email || '')
                setRole(response.data.role || DEFAULT_ROLE)
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = (data) => {
        props.onSubmit(data)
    }

    const handleRolesChange = (e) => {
        const role = e.target.value
        const isChecked = e.target.checked
        setRole(role)
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        setFieldValue(name, value)
    }


const setFieldValue = (fieldName, value) => {
    switch (fieldName) {
        case 'email':
            setEmail(value);
            setEmailErrorKey('usersEmailInvalid'); 
            setShowAlert(false); 
            break;
        case 'password':
            setPassword(value);
            if (value.length === 0) setPassErrorKey('usersPasswordInvalid');
            else if (value.length < 7) setPassErrorKey('usersPasswordShort');
            break;
        case 'name': setName(value); break;
        case 'vat': setVat(value); break;
        case 'phone': setPhone(value); break;
        default: break;
    }
};

const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    
    if (!form.checkValidity()) {
        setFormValid(true);
        return;
    }

    if (action === "add" || (action === "edit" && password.length > 0)) {
        if (password.length < 7) {
            setPassErrorKey(password.length === 0 ? 'usersPasswordInvalid' : 'usersPasswordShort');
            setFormValid(true);
            return;
        }
    }

    const user = { name, vat, phone, email, role };

    try {
        if (action === "add") {
            user.password = password;
            await UsersService.addUser(user);

        } else if (action === "edit") {
            if (password.length > 0){
                user.password = password
            } 
            await UsersService.updateUser(user, elementId);
        }
        else if (action === "delete") {
            await UsersService.deleteUser(elementId)
        }
        
        onSubmit(user); 
    } catch (error) {
        const serverMsg = error.response?.data?.msg;

        if (serverMsg === "usersEmailAlreadyExists") {
            setEmailErrorKey('usersEmailAlreadyExists'); 
            
            setFormValid(false); 
        } else {
            setFormValid(true);
        }
    }
};

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="name">
                    <Form.Label><FormattedMessage id="usersName" defaultMessage="Name" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="name"
                        value={name}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="usersNameInvalid" defaultMessage="Provide an user name" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="vat">
                    <Form.Label><FormattedMessage id="usersVAT" defaultMessage="VAT number" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="vat"
                        value={vat}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="usersVATInvalid" defaultMessage="Provide a valid VAT number" />
                    </Form.Control.Feedback>
                </Form.Group>

                {!props.noEditRole &&
                    <Form.Group className="mb-3" controlId="role">
                        <Form.Label><FormattedMessage id="usersRole" defaultMessage="Role" />:</Form.Label>
                        <div className="mb-2">
                            {USER_ROLES.map((userRole) => (
                                <Form.Check
                                    key={userRole}
                                    inline
                                    label={userRole}
                                    name="role"
                                    id={"checkb_" + userRole}
                                    value={userRole}
                                    type="radio"
                                    onChange={handleRolesChange}
                                    checked={role === userRole ? true : false} // Si el rol está en el array de roles seleccionados, se marca
                                    disabled={inputDisabled} // Desactivar si es necesario
                                />
                            ))}
                        </div>
                    </Form.Group>
                }

                <Form.Group className="mb-3" controlId="phone">
                    <Form.Label><FormattedMessage id="usersPhoneNumber" defaultMessage="Phone number" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="phone"
                        value={phone}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="usersPhoneNumberInvalid" defaultMessage="Provide a phone number" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="email">
                    <Form.Label><FormattedMessage id="usersEmail" defaultMessage="E-mail" />:</Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        value={email}
                        onChange={handleInputChange}
                        required="required"
                        pattern="^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$"
                        isInvalid={emailErrorKey === 'usersEmailAlreadyExists'}
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id={emailErrorKey} defaultMessage="Provide an Email address" />
                    </Form.Control.Feedback>
                </Form.Group>

                {action === "add" && (
                    <Form.Group className="mb-3" controlId="password">
                        <Form.Label><FormattedMessage id="usersPassword" defaultMessage="Password" />:</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={password}
                            onChange={handleInputChange}
                            disabled={inputDisabled && ("disabled")}
                            required="required"
                            minLength={7}
                        />
                       <Form.Control.Feedback type="invalid">
                            <FormattedMessage id={passErrorKey} defaultMessage="Provide an user password"/>
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                {action === "edit" && (
                    <Form.Group className="mb-3" controlId="password">
                        <Form.Label><FormattedMessage id="usersPassword" defaultMessage="Passworde" />:</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={password}
                            onChange={handleInputChange}
                            disabled={inputDisabled && ("disabled")}
                        />
                        <Form.Control.Feedback type="invalid">
                            <FormattedMessage id="usersPasswordInvalid" defaultMessage="Provide an user password" />
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default UsersForm

